// Pure helpers extracted from App: routing/error parsing, autosave flush, style/typography
// helpers and slug building. No React — safe to import anywhere.
import type { StyleProperties } from '@webbuilder/style-engine'
import type { ElementNode } from './store/editor.store'
import { DASHBOARD_URL } from './appConfig'
import { translate } from './lib/i18n'

/**
 * Por qué no se pudo abrir el proyecto, en CLAVES.
 *
 * Guarda claves y no frases por dos motivos que van juntos. El primero: esto se
 * arma en funciones sueltas, fuera de React, donde no hay traductor. El segundo,
 * y el que importa: si guardara el texto ya escrito, el efecto que abre el
 * proyecto tendría que depender del traductor — y entonces **cambiar de idioma
 * volvería a pedir el proyecto al servidor**. Con claves, el efecto no sabe nada
 * de idiomas y quien pinta el error lo dice en el de ahora.
 *
 * `message` es la excepción: cuando el servidor manda su propio texto, viaja tal
 * cual. Es lo único que no podemos traducir porque no sabemos qué va a decir.
 */
export type ProjectLoadError = {
  titleKey: string
  messageKey?: string
  /** Sólo cuando el texto lo pone el servidor. */
  message?: string
  vars?: Record<string, string | number>
}

export function dashboardLoginUrl() {
  const url = new URL(DASHBOARD_URL, window.location.origin)
  url.pathname = '/login'
  url.searchParams.set('redirectTo', window.location.href)
  return url.toString()
}

/**
 * La cuenta con la que el dashboard abrió el editor (`?uid=`).
 *
 * El editor guarda su sesión en su propio origen y sólo pide un handoff cuando
 * no tiene ninguna, así que una sesión vieja sobrevive a los cambios de cuenta
 * del dashboard: el proyecto —que sí es tuyo— respondía 403 y salía «este
 * proyecto no existe o no pertenece a tu cuenta». Con el identificador en la URL
 * el desajuste se detecta antes de pedir nada.
 */
export function expectedUserIdFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get('uid')
}

// Un solo reintento: rehacer el handoff arregla la sesión desajustada a la
// primera, así que si tras volver del dashboard el usuario sigue sin coincidir,
// el proyecto es de otra cuenta de verdad y toca enseñar el error en vez de
// rebotar para siempre.
const IDENTITY_ATTEMPTS_KEY = 'veltica_identity_attempts'
const MAX_IDENTITY_ATTEMPTS = 1

/** Consume un reintento de identidad. `false` cuando ya no quedan. */
export function takeIdentityRetry(): boolean {
  try {
    const attempts = Number(sessionStorage.getItem(IDENTITY_ATTEMPTS_KEY) ?? '0') + 1
    sessionStorage.setItem(IDENTITY_ATTEMPTS_KEY, String(attempts))
    return attempts <= MAX_IDENTITY_ATTEMPTS
  } catch {
    // Sin sessionStorage no hay forma de contar: mejor el error que el bucle.
    return false
  }
}

export function clearIdentityRetries() {
  try { sessionStorage.removeItem(IDENTITY_ATTEMPTS_KEY) } catch { /* private mode */ }
}

/**
 * Sólo se vuelve al dashboard: `returnTo` viaja en la URL y no es de fiar.
 */
export function dashboardReturnUrl(returnTo: string | null): string {
  const dashboard = new URL(DASHBOARD_URL, window.location.origin)
  if (returnTo) {
    try {
      const target = new URL(returnTo)
      if (target.origin === dashboard.origin) return target.toString()
    } catch { /* URL inválida — al dashboard tal cual */ }
  }
  return dashboard.toString()
}

export async function readProjectLoadError(res: Response): Promise<ProjectLoadError> {
  if (res.status === 400) {
    return { titleKey: 'load.invalidTitle', messageKey: 'load.invalidBody' }
  }
  if (res.status === 401) {
    return { titleKey: 'load.sessionTitle', messageKey: 'load.sessionBody' }
  }
  if (res.status === 403 || res.status === 404) {
    return { titleKey: 'load.unavailableTitle', messageKey: 'load.unavailableBody' }
  }

  try {
    const data = await res.json() as { error?: string; message?: string }
    const delServidor = data.message ?? data.error
    return delServidor
      ? { titleKey: 'save.cannotOpen', message: delServidor }
      : { titleKey: 'save.cannotOpen', messageKey: 'load.serverSaid', vars: { status: res.status } }
  } catch {
    return { titleKey: 'save.cannotOpen', messageKey: 'load.serverSaid', vars: { status: res.status } }
  }
}

export function flushAutosaveBeforeLeave(timeoutMs = 1200) {
  return new Promise<void>((resolve) => {
    let done = false
    const timeout = window.setTimeout(() => {
      if (done) return
      done = true
      resolve()
    }, timeoutMs)

    window.dispatchEvent(new CustomEvent('veltica:autosave-now', {
      detail: {
        done: () => {
          if (done) return
          done = true
          window.clearTimeout(timeout)
          resolve()
        },
      },
    }))
  })
}

export function mergeStylePatch(base: StyleProperties | undefined, patch: Partial<StyleProperties>): StyleProperties {
  const next = { ...(base ?? {}) }
  for (const key of Object.keys(patch) as (keyof StyleProperties)[]) {
    if (patch[key] === '') delete next[key]
    else next[key] = patch[key] as string
  }
  return next
}

export function countTypographyUsage(elements: ElementNode[], counts: Record<string, number>) {
  for (const element of elements) {
    const styleId = element.styleRefs?.typography
    if (styleId) counts[styleId] = (counts[styleId] ?? 0) + 1
    else if (element.themeRole) {
      const styleEntry = Object.entries({
        h1: 'heading1',
        h2: 'heading2',
        h3: 'heading3',
        h4: 'heading4',
        h5: 'heading5',
        h6: 'heading6',
        p: 'paragraph',
        small: 'eyebrow',
      }).find(([, role]) => role === element.themeRole)
      if (styleEntry) counts[styleEntry[0]] = (counts[styleEntry[0]] ?? 0) + 1
    }
    countTypographyUsage(element.children, counts)
  }
}

export function toPublicSubdomain(value: string, fallback = 'sitio'): string {
  const slug = value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  return slug || fallback
}

/**
 * El motivo por el que el servidor rechazó una publicación.
 *
 * Cuando falla la validación, el worker contesta `{ error: 'Validation failed',
 * issues: { campo: [...] } }`. Antes sólo se leía `error`, así que en pantalla
 * quedaba «Validation failed» a secas: un mensaje que dice que algo está mal y
 * ninguna de las dos cosas que hacen falta para arreglarlo —qué campo y por
 * qué—. Los `issues` traen exactamente eso y se estaban tirando.
 *
 * Se enseña el nombre del campo tal cual lo devuelve el servidor: es un dato
 * técnico, pero es lo que permite decir «el formulario tal no tiene nombre» en
 * vez de dejar a alguien mirando un sitio que no publica.
 */
export async function readPublishError(res: Response): Promise<string> {
  try {
    const data = await res.json() as {
      error?: string
      message?: string
      issues?: Record<string, string[] | undefined>
    }
    const base = data.message ?? data.error ?? translate('pub.failed')
    const issues = data.issues
      ? Object.entries(data.issues)
        .map(([field, messages]) => `${field}: ${(messages ?? []).join(', ')}`)
        .filter((line) => !line.endsWith(': '))
      : []
    return issues.length > 0 ? `${base} — ${issues.join(' · ')}` : base
  } catch {
    return translate('pub.failed')
  }
}

export async function readTemplateAdminError(res: Response): Promise<string> {
  try {
    const data = await res.json() as { error?: string; message?: string }
    const detail = data.message ?? data.error
    return detail ? `Template admin HTTP ${res.status}: ${detail}` : `Template admin HTTP ${res.status}`
  } catch {
    return `Template admin HTTP ${res.status}`
  }
}
